"use client";

import { useEffect, useSyncExternalStore } from "react";
import { readCookieConsent } from "@/components/analytics/browser-attribution";
import { COOKIE_CONSENT_NAME } from "@/lib/cookie-consent";

declare global {
  interface Window {
    fbq?: MetaFbq;
    _fbq?: MetaFbq;
  }
}

type MetaFbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  push?: MetaFbq;
  loaded?: boolean;
  version?: string;
};

/**
 * Meta (Facebook) Pixel — retargeting tag for the marketing + funnel + ads
 * surfaces.
 *
 * Env-gated on NEXT_PUBLIC_META_PIXEL_ID. When unset (the default — operator
 * must create a Meta Business account and provision the pixel ID before this
 * is wired live), this component renders nothing and is a complete no-op.
 *
 * Consent-gated like components/analytics/google-analytics.tsx +
 * microsoft-clarity.tsx: the <Script> tag is NOT rendered until
 * cookie_consent === "accepted". PECR/ICO compliance — Meta's fbevents.js
 * drops `_fbp` first-party tracking cookies and posts a PageView event the
 * instant it loads, BEFORE any consent API call could take effect.
 *
 * Mount scope: marketing, funnel, ads layouts ONLY. NOT mounted in (app),
 * (auth), or (onboarding) — authed/post-purchase users don't need
 * retargeting (they've already converted) and we don't want to spend
 * retargeting budget on them.
 *
 * Companion tag to the Google Ads remarketing tag (AW-18049615348) loaded by
 * components/analytics/google-analytics.tsx. See GitHub issue #8.
 *
 * Strategy: lazyOnload. The pixel runs purely for retargeting and doesn't
 * need to fire before user idle.
 */
function subscribeToConsent(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key === COOKIE_CONSENT_NAME) cb();
  };
  window.addEventListener("cookie_consent_change", cb);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("cookie_consent_change", cb);
    window.removeEventListener("storage", onStorage);
  };
}

function getServerConsentSnapshot(): "accepted" | "declined" | null {
  return null;
}

function getClientConsentSnapshot(): "accepted" | "declined" | null {
  return readCookieConsent();
}

export function MetaPixel() {
  const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID;

  const consent = useSyncExternalStore(
    subscribeToConsent,
    getClientConsentSnapshot,
    getServerConsentSnapshot,
  );

  useEffect(() => {
    if (!pixelId) return;
    if (consent !== "accepted") {
      window.fbq?.("consent", "revoke");
      return;
    }

    let active = true;
    let script = document.getElementById(
      "meta-pixel-lib",
    ) as HTMLScriptElement | null;

    function activate() {
      // Consent may have changed in another tab while fbevents.js was in
      // flight. Re-read the authoritative cookie at the final execution
      // boundary; never queue init/PageView before the library has loaded.
      if (!active || readCookieConsent() !== "accepted" || !window.fbq) {
        window.fbq?.("consent", "revoke");
        return;
      }
      window.fbq("init", pixelId);
      window.fbq("consent", "grant");
      window.fbq("track", "PageView");
    }

    if (window.fbq?.callMethod) {
      activate();
      return () => {
        active = false;
        window.fbq?.("consent", "revoke");
      };
    }

    if (!window.fbq) {
      const fbq = ((...args: unknown[]) => {
        if (fbq.callMethod) fbq.callMethod(...args);
        else fbq.queue?.push(args);
      }) as MetaFbq;
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = "2.0";
      fbq.queue = [];
      window.fbq = fbq;
      window._fbq = fbq;
    }

    const onLoad = () => {
      if (script) script.dataset.loaded = "true";
      activate();
    };

    if (script?.dataset.loaded === "true") {
      activate();
    } else {
      if (!script) {
        script = document.createElement("script");
        script.id = "meta-pixel-lib";
        script.async = true;
        script.src = "https://connect.facebook.net/en_US/fbevents.js";
        document.head.appendChild(script);
      }
      script.addEventListener("load", onLoad, { once: true });
    }

    return () => {
      active = false;
      script?.removeEventListener("load", onLoad);
      if (script?.dataset.loaded !== "true") script?.remove();
      window.fbq?.("consent", "revoke");
    };
  }, [consent, pixelId]);

  if (!pixelId || consent !== "accepted") return null;

  return (
    <noscript>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        height="1"
        width="1"
        style={{ display: "none" }}
        alt=""
        src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`}
      />
    </noscript>
  );
}
